import { PageUrls } from '@core/page-urls'
import React from 'react'
import { Button } from '@common/material'
import { localizedStrings } from '@core/strings'
import { RejectChecklistModal } from './modals/RejectChecklistModal'
import { ApproveChecklistModal } from './modals/ApproveChecklistModal'
import { Box, Flex } from '@fundamentals'
import { RequestChecklistChangesModal } from './modals/RequestChecklistChangesModal'
import { ActionCard } from '@elements'
import { useModal } from '@common/GlobalModal'
import { useRouter } from 'next/router'
import { Checklist } from '@core/entities/checklist/Checklist'

type ReviewChecklistCardProps = {
  organisationId: number
  projectId?: number
  checklist: Checklist
}

export const ReviewChecklistCard: React.FC<ReviewChecklistCardProps> = ({
  organisationId,
  projectId,
  checklist,
}) => {
  const { showModal } = useModal()
  const router = useRouter()
  if (!checklist.hasPermission('APPROVE')) return null

  const approvalData = checklist.approvalReviews?.approvals.find(
    ({ approval }) => approval.id === checklist.approvalReviews.nextApprovalId,
  )

  const hasQuestionsStep =
    approvalData?.approval?.approvalQuestions &&
    approvalData?.approval?.approvalQuestions?.questions.length > 0

  return (
    <ActionCard
      title={localizedStrings.approvalRequest}
      info={localizedStrings.approvalRequestInfoChecklist}
      BottomComponent={
        <>
          {checklist.hasPermission('REQUEST_CHANGES') && (
            <Button
              data-test='request-changes'
              variant='outlined'
              fullWidth
              onClick={() =>
                showModal(
                  <RequestChecklistChangesModal checklist={checklist} />,
                )
              }
            >
              {localizedStrings.requestChanges}
            </Button>
          )}
          <Flex gap={1}>
            {checklist.hasPermission('REJECT') && (
              <Box sx={{ flex: 1 }}>
                <Button
                  data-test='reject-checklist'
                  variant='outlined'
                  fullWidth
                  color='error'
                  onClick={() =>
                    showModal(<RejectChecklistModal checklist={checklist} />)
                  }
                >
                  {localizedStrings.reject}
                </Button>
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Button
                data-test='approve-checklist'
                variant='contained'
                fullWidth
                onClick={() => {
                  if (!hasQuestionsStep) {
                    return showModal(
                      <ApproveChecklistModal checklist={checklist} />,
                    )
                  }
                  if (projectId) {
                    router.push(
                      PageUrls.projectChecklistApproverQuestions(
                        organisationId,
                        projectId,
                        checklist.id,
                      ),
                    )
                  } else {
                    router.push(
                      PageUrls.checklistApproverQuestions(
                        organisationId,
                        checklist.id,
                      ),
                    )
                  }
                }}
              >
                {localizedStrings.approve}
              </Button>
            </Box>
          </Flex>
        </>
      }
      sx={{ mb: 1.5, borderColor: 'primary.main', borderWidth: 2 }}
    />
  )
}
